<script setup>
import { computed, ref } from 'vue';
import { useQuery } from 'villus';
import { gsap } from 'gsap';
import shuffle from 'lodash-es/shuffle';
import flatMap from 'lodash-es/flatMap';
import map from 'lodash-es/map';
import wysiwyg from '@/components/wysiwyg/Wysiwyg.vue';
import { useStore } from '@/store/Store.js';

defineProps({
    data: {
        type: Object,
        default: () => {},
    },
    color: {
        type: String,
        default: '',
    },
    themeColor: {
        type: String,
        default: '',
    },
    mainColor: {
        type: String,
        default: '',
    },
    center: {
        type: Boolean,
        default: false,
    },
});

const store = useStore();
const variables = computed(() => ({ lang: store.lang.value }));

let questionsAnswers = [];
const current = ref(null);
const mouse = ref(false);
const cursor = ref(null);
let index = 0;
const change = () => {
    current.value = questionsAnswers[index];
    if (index >= questionsAnswers.length - 1) {
        index = 0;
    } else {
        index++;
    }
};

const syncCursor = (e) => {
    gsap.to(cursor.value, { x: e.offsetX, y: e.offsetY });
};

const { data: mainData } = await useQuery({
    variables,
    query: `query FetchEntries($lang: [String]) {
        infos: entry(section: "home", site: $lang) {
            ... on home_Entry {
                title: homeFaqTitle
                cta: homeFaqCta
                next: homeFaqCtaNext
                nextMobile: homeFaqCtaNextMobile
            }
        }
        blocks: entries(section: "faq", site: $lang) {
            ... on faq_Entry {
                faqQuestionsAnswers {
                    ... on faqQuestionsAnswers_BlockType {
                        question
                        answer
                    }
                }
            }
        }
    }
`,
});

questionsAnswers = shuffle(flatMap(map(mainData.value.blocks, (v) => v.faqQuestionsAnswers)));
change();
</script>
<template>
    <div
        class="HomeFaq Component my-0 py-18 text-white xl:py-30"
        :class="[
            'bg-dark-' + mainColor,
            {
                'cursor-none': mouse && landscape,
            },
        ]"
        v-on:click="change"
    >
        <template v-if="mainData">
            <div class="relative mx-6 flex flex-col justify-between xl:mx-20 xl:flex-row xl:gap-6">
                <div class="HomeFaq__left w-full shrink-0 grow-0 xl:w-5/12">
                    <h2
                        class="mb-4 xl:mb-25"
                        :class="{ h2: xl || md, h3: xs }"
                    >
                        <div
                            v-if="xl"
                            v-html="mainData.infos.title"
                        />
                        <div
                            v-if="(xs || md) && current"
                            v-html="current.question"
                        />
                    </h2>
                    <div
                        v-if="xs || md"
                        class="HomeFaq__next mb-5 flex items-center"
                    >
                        <svg
                            class="mr-4 size-10 fill-current"
                            viewBox="0 0 50 50"
                        >
                            <path d="M25,50A25,25,0,1,1,50,25,25,25,0,0,1,25,50ZM25,2A23,23,0,1,0,48,25,23,23,0,0,0,25,2Z" />
                            <path
                                d="M33,18.88a5.22,5.22,0,0,1-1.15,2.47.78.78,0,0,0,1.1,1.1L37.41,18,33,13.55a.78.78,0,0,0-1.1,1.1A5.17,5.17,0,0,1,33,17.18a9.87,9.87,0,0,0-8.27,4.43c.35.56.66,1.11,1,1.64A8.25,8.25,0,0,1,33,18.88Z"
                            />
                            <path
                                d="M31.85,27.55a.77.77,0,0,0,0,1.1A5.22,5.22,0,0,1,33,31.12c-5.18-.32-6.71-3.16-8.46-6.43-1.87-3.48-4-7.42-11.1-7.42a.85.85,0,1,0,0,1.7c5.81,0,7.56,2.75,9.33,6-1.77,3.28-3.52,6-9.33,6a.85.85,0,0,0,0,1.7c6.14,0,8.55-2.93,10.29-6,1.64,2.9,3.85,5.74,9.28,6.05a5.17,5.17,0,0,1-1.16,2.53.78.78,0,0,0,1.1,1.1L37.41,32,33,27.55A.77.77,0,0,0,31.85,27.55Z"
                            />
                        </svg>
                        <h6 class="h6">
                            {{ mainData.infos.nextMobile }}
                        </h6>
                    </div>
                    <router-link
                        v-if="xl"
                        class="HomeFaq__link relative z-2 mt-12 xl:absolute xl:mt-0"
                        :to="{ name: 'faqs.' + lang }"
                    >
                        <Btn
                            :class="['bg-light-' + mainColor, 'text-dark-' + mainColor]"
                            :text="mainData.infos.cta"
                        />
                    </router-link>
                </div>
                <transition
                    enter-active-class="transition-opacity duration-500"
                    leave-active-class="transition-opacity duration-500"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                    mode="out-in"
                >
                    <div
                        v-if="current"
                        :key="current.question"
                        class="HomeFaq__right w-full pr-8 xl:w-7/12 xl:pl-8"
                    >
                        <h5
                            v-if="xl"
                            class="h5 mb-4 xl:mb-8"
                            v-html="current.question"
                        />
                        <wysiwyg
                            class="mx-0"
                            :space="false"
                            :data="{ data: { wysiwyg: current.answer } }"
                        />
                        <router-link
                            v-if="xs || md"
                            class="HomeFaq__link"
                            :to="{ name: 'faqs.' + lang }"
                        >
                            <Btn
                                :class="['bg-light-' + mainColor, 'text-dark-' + mainColor]"
                                :text="mainData.infos.cta"
                            />
                        </router-link>
                    </div>
                </transition>
            </div>
        </template>

        <template v-if="mainData">
            <div
                class="HomeFaq__mouse-catcher absolute inset-0 z-1 hidden xl:block"
                v-on:mousemove="syncCursor"
                v-on:mouseenter="mouse = true"
                v-on:mouseleave="mouse = false"
            >
                <transition
                    enter-active-class="transition-opacity duration-500"
                    leave-active-class="transition-opacity duration-500"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="mouse"
                        ref="cursor"
                        class="HomeFaq__cursor pointer-events-none absolute left-0 top-0 -ml-26 -mt-26 size-52"
                    >
                        <svg
                            class="fill-current"
                            viewBox="0 0 210 210"
                            :class="['text-light-' + mainColor]"
                        >
                            <path
                                d="M121.42,94.1a9.12,9.12,0,0,1-2,4.35,1.38,1.38,0,0,0,0,2,1.39,1.39,0,0,0,2,0l7.85-7.85-7.85-7.85a1.38,1.38,0,0,0-2,2,9,9,0,0,1,2,4.45c-7.76.43-11.78,3.82-14.56,7.82.61,1,1.16,2,1.67,2.89C111.07,97.7,114.28,94.54,121.42,94.1Z"
                            />
                            <path
                                d="M119.39,109.38a1.38,1.38,0,0,0,0,2,9.12,9.12,0,0,1,2,4.35c-9.14-.56-11.83-5.58-14.92-11.34-3.29-6.13-7-13.07-19.55-13.07a1.5,1.5,0,0,0,0,3c10.24,0,13.31,4.83,16.44,10.62-3.13,5.79-6.2,10.63-16.44,10.63a1.5,1.5,0,0,0,0,3c10.81,0,15.06-5.16,18.13-10.51,2.9,5.11,6.79,10.12,16.36,10.66a9.11,9.11,0,0,1-2,4.47,1.38,1.38,0,0,0,2,1.94l7.85-7.85-7.85-7.85A1.39,1.39,0,0,0,119.39,109.38Z"
                            />
                            <path d="M105,210A105,105,0,1,1,210,105,105.12,105.12,0,0,1,105,210ZM105,2A103,103,0,1,0,208,105,103.12,103.12,0,0,0,105,2Z" />
                        </svg>
                        <div
                            class="HomeFaq__cursor-label absolute left-1/2 top-3/4 w-full -translate-x-1/2 -translate-y-1/2 text-center"
                            :class="['text-light-' + mainColor]"
                        >
                            {{ mainData.infos.next }}
                        </div>
                    </div>
                </transition>
            </div>
        </template>
    </div>
</template>
